/**
 * Settings page.
 * Contains recovery code and restore functionality.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRecoveryCode, restoreProgress } from '../api/client';
import './Settings.css';

export function Settings() {
    const navigate = useNavigate();
    const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
    const [restoreCode, setRestoreCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState<{ message: string; isError?: boolean } | null>(null);

    const handleGetRecoveryCode = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchRecoveryCode();
            setRecoveryCode(response.recovery_code);
        } catch {
            setToast({ message: 'Failed to get recovery code', isError: true });
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCopyCode = useCallback(async () => {
        if (!recoveryCode) return;
        try {
            await navigator.clipboard.writeText(recoveryCode);
            setToast({ message: 'Copied!' });
            setTimeout(() => setToast(null), 2000);
        } catch {
            setToast({ message: 'Failed to copy', isError: true });
        }
    }, [recoveryCode]);

    const handleRestore = useCallback(async () => {
        if (!restoreCode.trim()) return;
        setLoading(true);
        try {
            await restoreProgress(restoreCode.trim());
            setToast({ message: 'Restored' });
            setTimeout(() => {
                navigate('/daily');
                window.location.reload();
            }, 1000);
        } catch {
            setToast({ message: 'Invalid code', isError: true });
        } finally {
            setLoading(false);
        }
    }, [restoreCode, navigate]);

    return (
        <div className="page settings-page">
            <div className="page-content">
                <header className="settings-header">
                    <h1>Settings</h1>
                </header>

                <section className="settings-section">
                    <h2>Recovery Code</h2>
                    <p className="settings-description">
                        Save this code. It restores your streak if you clear cookies.
                    </p>

                    {recoveryCode ? (
                        <div className="recovery-display">
                            <code className="recovery-code">{recoveryCode}</code>
                            <button className="settings-button" onClick={handleCopyCode}>
                                Copy
                            </button>
                        </div>
                    ) : (
                        <button
                            className="settings-button settings-button--primary"
                            onClick={handleGetRecoveryCode}
                            disabled={loading}
                        >
                            {loading ? 'Loading...' : 'Get Recovery Code'}
                        </button>
                    )}
                </section>

                <section className="settings-section">
                    <h2>Restore Progress</h2>
                    <div className="restore-form">
                        <input
                            type="text"
                            className="restore-input"
                            placeholder="XXXX-XXXX-XXXX-XXXX"
                            value={restoreCode}
                            onChange={(e) => setRestoreCode(e.target.value)}
                        />
                        <button
                            className="settings-button settings-button--primary"
                            onClick={handleRestore}
                            disabled={loading || !restoreCode.trim()}
                        >
                            Restore
                        </button>
                    </div>
                </section>

                <button className="back-button" onClick={() => navigate('/daily')}>
                    Back to Daily
                </button>
            </div>

            {toast && (
                <div className={`toast ${toast.isError ? 'toast--error' : ''}`}>
                    {toast.message}
                </div>
            )}
        </div>
    );
}
